// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getIsPunchOutSession, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentAccountState } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import loadCurrentAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/LoadCurrentAccount";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadSystemList from "@synd/App/Handlers/SystemList/LoadSystemList";
import { SystemListModel } from "@synd/App/Services/SystemListService";
import { CustomAppState } from "@synd/App/Store/Reducers";
import { xHasRole, xIsAuthenticated } from "@synd/App/Store/Selectors/GeneralSelectors";
import { getSystemList } from "@synd/App/Store/Selectors/SystemListSelectors";
import { USER_ROLES } from "@synd/XDL/constants";
import { mergeWithCss, xMask } from "@synd/XDL/Utilities";
import React, { FC, useEffect, useMemo } from "react";
import { connect, ResolveThunks } from "react-redux";

interface MiniSignInProps {
    accountManagementLinks?: PageLinkModel[];
    orderManagementLinks?: PageLinkModel[];
    displayChangeCustomerLink?: boolean;
    onSignOutHandler?: () => void;
    extendedStyles?: MiniMyAccountStyles;
}

const mapStateToProps = (state: CustomAppState) => {
    const session = getSession(state);
    return {
        systemList: getSystemList(state, "Marketing Segment Contact Info"),
        currentBillTo: getBillToState(state, session.billToId).value,
        isPunchOutSession: getIsPunchOutSession(state),
        isAuthenticated: xIsAuthenticated(state),
        shipTo: getShipToState(state, session.shipToId).value,
        changeCustomerPageUrl: getPageLinkByPageType(state, "ChangeCustomerPage")?.url,
        account: getCurrentAccountState(state).value,
        accountLoading: getCurrentAccountState(state).isLoading,
        isRetailer: xHasRole(state, USER_ROLES.RETAILER),
        session,
    };
};

const mapDispatchToProps = {
    loadCurrentAccount,
    loadSystemList,
};

type Props = MiniSignInProps &
    HasHistory &
    ResolveThunks<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps>;

export interface MiniMyAccountStyles {
    wrapper?: InjectableCss;
    sectionTitle?: TypographyPresentationProps;
    linksContainer?: InjectableCss;
    linksSection?: InjectableCss;
    link?: LinkPresentationProps;
    infoContainer?: InjectableCss;
    infoSections?: InjectableCss;
    infoSectionTitle?: TypographyPresentationProps;
    infoValue?: TypographyPresentationProps;
    actionsContainer?: InjectableCss;
    changeShiptoLink?: ButtonPresentationProps;
    signOutButton?: ButtonPresentationProps;
    profilePersonWrapper?: InjectableCss;
    salesPersonWrapper?: InjectableCss;
    infoSection?: InjectableCss;
    addressInfo?: AddressInfoDisplayStyles;
    infoLink?: LinkPresentationProps;
}

export const miniMyAccountStyles: MiniMyAccountStyles = {};

const MiniMyAccount: FC<Props> = ({
    accountManagementLinks,
    orderManagementLinks,
    displayChangeCustomerLink,
    loadCurrentAccount,
    loadSystemList,
    systemList,
    currentBillTo,
    isPunchOutSession,
    isAuthenticated,
    shipTo,
    changeCustomerPageUrl,
    account,
    accountLoading,
    isRetailer,
    session,
    onSignOutHandler,
    extendedStyles,
    history,
}) => {
    const [styles] = React.useState(() => mergeWithCss(miniMyAccountStyles, extendedStyles));

    const { salesRepEmail, salesRepPhone } = session.properties;

    const myFilteredAccountManagementLinks = accountManagementLinks?.filter(
        pageLink => pageLink && !(pageLink.type === "ChangeCustomerPage" && !displayChangeCustomerLink),
    );

    const myFilteredOrderManagementLinks = orderManagementLinks?.filter(
        pageLink => pageLink && !(pageLink.type === "ChangeCustomerPage" && !displayChangeCustomerLink),
    );

    const handleNavigateToChangeCustomer = () => {
        if (changeCustomerPageUrl) {
            history.push(changeCustomerPageUrl);
        }
    };

    useEffect(() => {
        if (!account && isAuthenticated && !accountLoading) {
            loadCurrentAccount();
        }
    }, [account, isAuthenticated, accountLoading, loadCurrentAccount]);

    useEffect(() => {
        if (!systemList && isAuthenticated)
            loadSystemList({
                name: "Marketing Segment Contact Info",
            });
    }, [systemList, isAuthenticated, loadSystemList]);

    const segmentRep = React.useMemo(() => {
        if (!systemList) {
            return null;
        }
        let match = systemList?.find((l: SystemListModel) => l.name === currentBillTo?.properties?.segment);
        if (!match || isRetailer) {
            match = systemList.find((v: SystemListModel) => v.name === "Default");
        }
        if (!match?.description) {
            return null;
        }
        const [email, phone] = match.description.split(",").map((s: string) => s.trim());
        return { email, phone: xMask.phoneNumber(phone) };
    }, [systemList, currentBillTo?.properties?.segment]);
    const maskedPhoneNumber = useMemo(() => xMask.phoneNumber(salesRepPhone), [salesRepPhone]);

    const linksContainer = (title: string, links: PageLinkModel[] | undefined) => {
        return (
            <StyledWrapper {...styles.linksContainer}>
                <Typography {...styles.sectionTitle}>{title}</Typography>
                <StyledWrapper {...styles.linksSection}>
                    {links?.map(
                        link =>
                            link?.url && (
                                <Link key={`${link.title}_${link.url}`} href={link?.url} {...styles.link}>
                                    {link?.title}
                                </Link>
                            ),
                    )}
                </StyledWrapper>
            </StyledWrapper>
        );
    };
    return (
        <>
            <StyledWrapper {...styles.wrapper} data-test-selector="header-mini-myAccount">
                {linksContainer(translate("Account Management"), myFilteredAccountManagementLinks)}
                {linksContainer(translate("Product & Order Management"), myFilteredOrderManagementLinks)}
                <StyledWrapper {...styles.infoContainer}>
                    <Typography {...styles.sectionTitle}>{translate("Profile Information")}</Typography>
                    <StyledWrapper {...styles.infoSections}>
                        <StyledWrapper {...styles.infoSection}>
                            <Typography {...styles.infoSectionTitle}>{shipTo?.companyName}</Typography>
                            {!!session?.email && (
                                <Link {...styles.infoLink} href={`mailto:${session.email}`}>
                                    {session.email}
                                </Link>
                            )}
                            {!!account?.properties.phone && (
                                <Link {...styles.infoLink} href={`tel:${account.properties.phone}`}>
                                    {account.properties.phone}
                                </Link>
                            )}
                        </StyledWrapper>
                        <StyledWrapper {...styles.infoSection}>
                            <Typography {...styles.infoSectionTitle}>{translate("Default Account Address")}</Typography>
                            {shipTo && (
                                <AddressInfoDisplay
                                    address1={shipTo.address1}
                                    address2={shipTo.address2}
                                    city={shipTo.city}
                                    state={shipTo.state?.abbreviation}
                                    postalCode={shipTo.postalCode}
                                    extendedStyles={styles.addressInfo}
                                />
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>

                <StyledWrapper {...styles.infoContainer}>
                    <Typography {...styles.sectionTitle} className="mb-4">
                        {translate("Customer Support")}
                    </Typography>
                    <StyledWrapper {...styles.infoSections}>
                        {(!!segmentRep?.phone || !!segmentRep?.email) && (
                            <StyledWrapper {...styles.infoSection}>
                                <Typography {...styles.infoSectionTitle}>{translate("Customer Experience")}</Typography>
                                {segmentRep.email && (
                                    <Link {...styles.infoLink} href={`mailto:${segmentRep.email}`}>
                                        {segmentRep.email}
                                    </Link>
                                )}
                                {segmentRep.phone && (
                                    <Link {...styles.infoLink} href={`tel:${segmentRep.phone}`}>
                                        {segmentRep.phone}
                                    </Link>
                                )}
                            </StyledWrapper>
                        )}

                        {(!!salesRepPhone || !!salesRepEmail || !!isRetailer) && (
                            <StyledWrapper {...styles.infoSection}>
                                <Typography {...styles.infoSectionTitle}>
                                    {translate("Sales Representative")}
                                </Typography>
                                {isRetailer ? (
                                    <Typography {...styles.infoSectionTitle}>
                                        {siteMessage("Retail_Contact_Wholesaler_Message")}
                                    </Typography>
                                ) : (
                                    <>
                                        {salesRepEmail && (
                                            <Link {...styles.infoLink} href={`mailto:${salesRepEmail}`}>
                                                {salesRepEmail}
                                            </Link>
                                        )}
                                        {salesRepPhone && (
                                            <Link {...styles.infoLink} href={`tel:${salesRepPhone}`}>
                                                {maskedPhoneNumber}
                                            </Link>
                                        )}
                                    </>
                                )}
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.actionsContainer}>
                {changeCustomerPageUrl && displayChangeCustomerLink && (
                    <Button
                        {...styles.changeShiptoLink}
                        onClick={handleNavigateToChangeCustomer}
                        aria-label="Change Ship-to"
                        data-test-selector="headerChangeShipToLink"
                    >
                        {translate("Switch Account")}
                    </Button>
                )}
                <Button {...styles.signOutButton} onClick={onSignOutHandler} data-test-selector="headerSignOutLink">
                    {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                </Button>
            </StyledWrapper>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(MiniMyAccount));
